/**
 * TestSimulationPanel Component
 * 
 * Allows admins to test different commission rates and CA values
 * before making GO/NO GO/Counter-proposal decisions
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
};

const TestSimulationPanel = ({ 
  simulationId, 
  simulation, 
  language = 'fr',
  onResultChange 
}) => {
  const { api } = useAuth();
  const [showPanel, setShowPanel] = useState(false);
  const [testData, setTestData] = useState({
    commission_rate: simulation?.commission_rate_proposed || 0.18,
    ca_previsionnel_ht: simulation?.ca_previsionnel_ht_proposed || ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTestSimulation = async () => {
    if (!testData.ca_previsionnel_ht || testData.ca_previsionnel_ht <= 0) {
      toast.error(language === 'fr' ? 'Veuillez saisir un CA HT valide' : 'Please enter a valid CA HT');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api().post(`/simulations/${simulationId}/test-simulation`, {
        commission_rate: testData.commission_rate,
        ca_previsionnel_ht: parseFloat(testData.ca_previsionnel_ht)
      });
      setResult(response.data);
      if (onResultChange) onResultChange(response.data);
      toast.success(language === 'fr' 
        ? `Simulation de test effectuée` 
        : `Test simulation completed`);
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la simulation' : 'Simulation failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePanelToggle = () => {
    setShowPanel(!showPanel);
    if (!showPanel && simulation) {
      setTestData({
        commission_rate: simulation.commission_rate_proposed || 0.18,
        ca_previsionnel_ht: simulation.ca_previsionnel_ht_proposed || ''
      });
    }
  };

  return (
    <div className="p-2 bg-violet-50 dark:bg-violet-900/20 rounded border border-violet-200 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-violet-700 dark:text-violet-400 flex items-center gap-1">
          <Calculator className="w-3 h-3" />
          {language === 'fr' ? 'Simulation de Test' : 'Test Simulation'}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[10px] text-violet-600 hover:text-violet-700 p-1"
          onClick={handlePanelToggle}
        >
          {showPanel 
            ? (language === 'fr' ? 'Masquer' : 'Hide') 
            : (language === 'fr' ? 'Tester avant décision' : 'Test before decision')}
        </Button>
      </div>
      
      {showPanel && (
        <div className="space-y-3 pt-2 border-t border-violet-200">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] text-violet-700">
                {language === 'fr' ? 'Commission (%)' : 'Commission (%)'}
              </Label>
              <Select 
                value={testData.commission_rate.toString()} 
                onValueChange={(v) => setTestData(prev => ({...prev, commission_rate: parseFloat(v)}))}
              >
                <SelectTrigger className="bg-white dark:bg-slate-800 h-8 text-xs border-violet-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25].map(pct => (
                    <SelectItem key={pct} value={(pct / 100).toString()}>{pct}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-violet-700">
                {language === 'fr' ? 'CA HT (€)' : 'Revenue (€)'}
              </Label>
              <Input
                type="number"
                placeholder="500000"
                value={testData.ca_previsionnel_ht}
                onChange={(e) => setTestData(prev => ({...prev, ca_previsionnel_ht: e.target.value}))}
                className="bg-white dark:bg-slate-800 h-8 text-xs border-violet-300"
              />
            </div>
          </div>
          
          <Button 
            onClick={runTestSimulation}
            disabled={loading}
            size="sm"
            className="w-full bg-violet-600 hover:bg-violet-700 h-8 text-xs"
            data-testid="run-test-simulation-btn"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-3 h-3 mr-1" />
            )}
            {language === 'fr' ? 'Lancer la simulation' : 'Run simulation'}
          </Button>
          
          {/* Test Simulation Results */}
          {result && (
            <TestSimulationResults 
              result={result} 
              language={language} 
              formatCurrency={formatCurrency}
              onClear={() => setResult(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const TestSimulationResults = ({ result, language, formatCurrency, onClear }) => (
  <div className="mt-3 space-y-2">
    {/* Verdict Badge */}
    <div className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border">
      <span className="text-xs text-slate-600">
        {language === 'fr' ? 'Verdict simulé:' : 'Simulated verdict:'}
      </span>
      <Badge className={`text-xs font-bold ${
        result.verdict === 'GO' ? 'bg-emerald-500 text-white' :
        result.verdict === 'A_ETUDE' ? 'bg-amber-500 text-white' :
        'bg-red-500 text-white'
      }`}>
        {result.verdict === 'GO' ? 'GO' : 
         result.verdict === 'A_ETUDE' ? "À L'ÉTUDE" : 'NO GO'}
      </Badge>
    </div>
    
    {/* Key Metrics */}
    <div className="grid grid-cols-3 gap-1 text-center">
      <div className="p-2 bg-white dark:bg-slate-800 rounded border">
        <p className="text-[9px] text-slate-500">
          {language === 'fr' ? 'Mois rouges' : 'Red months'}
        </p>
        <p className={`text-sm font-bold ${
          result.red_months_count <= 3 ? 'text-emerald-600' :
          result.red_months_count <= 5 ? 'text-amber-600' : 'text-red-600'
        }`}>
          {result.red_months_count}/12
        </p>
      </div>
      <div className="p-2 bg-white dark:bg-slate-800 rounded border">
        <p className="text-[9px] text-slate-500">Payback</p>
        <p className="text-sm font-bold text-slate-700">
          {result.payback_months} {language === 'fr' ? 'mois' : 'mo'}
        </p>
      </div>
      <div className="p-2 bg-white dark:bg-slate-800 rounded border">
        <p className="text-[9px] text-slate-500">Cluster</p>
        <p className="text-xs font-bold text-slate-700">
          {result.test_parameters?.cluster_used}
        </p>
      </div>
    </div>
    
    {/* Monthly Breakdown Mini Table */}
    <div className="p-2 bg-white dark:bg-slate-800 rounded border">
      <p className="text-[10px] font-semibold text-slate-600 mb-2">
        {language === 'fr' ? 'Analyse Mensuelle Y1 (test)' : 'Monthly Analysis Y1 (test)'}
      </p>
      <div className="flex gap-0.5">
        {result.monthly_breakdown_y1?.map((month, idx) => (
          <div
            key={idx}
            className={`flex-1 h-6 rounded text-[8px] flex items-center justify-center font-medium ${
              month.is_negative 
                ? 'bg-red-500 text-white' 
                : 'bg-emerald-500 text-white'
            }`}
            title={`M${month.month}: ${formatCurrency(month.cash_estimate)}`}
          >
            M{month.month}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-slate-500">
        <span>Food Cost: {((result.test_parameters?.food_cost_rate || 0) * 100).toFixed(1)}%</span>
        <span>Other Cost: {((result.test_parameters?.other_cost_rate || 0) * 100).toFixed(1)}%</span>
      </div>
    </div>
    
    {/* Clear button */}
    <Button
      variant="ghost"
      size="sm"
      className="w-full h-6 text-[10px] text-slate-500"
      onClick={onClear}
    >
      {language === 'fr' ? 'Effacer le résultat' : 'Clear result'}
    </Button>
  </div>
);

export default TestSimulationPanel;
